import React, { useMemo } from 'react';
import { Divider, Flex, Form, Switch } from 'antd';
import { AlertTriangle, Bike, Eye, EyeOff, ShoppingBag } from 'lucide-react';

/**
 * Store-level availability editor shared by the product and modifier modals.
 * `t` must be scoped to a keyPrefix that exposes `availability.*` and
 * `form.is_visible` / `form.is_available_for_pickup` / `form.is_available_for_delivery`.
 */
export default function AvailabilityCard({ data, update, t }) {
  const visible = Boolean(data.is_visible);
  const pickup = Boolean(data.is_available_for_pickup);
  const delivery = Boolean(data.is_available_for_delivery);

  function channelSubtitleKey(field, isOn) {
    if (!visible) {
      return 'availability.placeholder';
    }

    return isOn ? `form.${field}.subtitle_on` : `form.${field}.subtitle_off`;
  }

  const status = useMemo(() => {
    if (!visible) {
      return { key: 'hidden', bg: 'rgb(243, 244, 246)', color: 'rgb(107, 114, 128)' };
    }

    if (pickup && delivery) {
      return { key: 'both', bg: 'rgb(232, 248, 240)', color: 'rgb(15, 168, 99)' };
    }

    if (pickup) {
      return { key: 'pickup_only', bg: 'rgb(255, 247, 237)', color: 'rgb(194, 65, 12)' };
    }

    if (delivery) {
      return { key: 'delivery_only', bg: 'rgb(239, 246, 255)', color: 'rgb(29, 78, 216)' };
    }

    return { key: 'no_channels', bg: 'rgb(254, 243, 199)', color: 'rgb(146, 64, 14)' };
  }, [visible, pickup, delivery]);

  const showNoChannelsWarning = visible && !pickup && !delivery;

  return (
    <Form.Item>
      <div
        className="rounded-md bg-white mt-2"
        style={{ padding: '14px 16px 16px 16px', border: '1px solid rgb(229, 231, 235)' }}
      >
        <Flex justify="space-between" align="baseline" className="mb-1">
          <span className="font-semibold" style={{ fontSize: 13, letterSpacing: 0.13, color: 'rgb(17, 24, 39)' }}>
            {t('availability.title')}
          </span>
          <span
            className="font-medium"
            style={{
              fontSize: 11,
              padding: '2px 8px',
              borderRadius: 999,
              backgroundColor: status.bg,
              color: status.color,
            }}
          >
            {t(`availability.status.${status.key}`)}
          </span>
        </Flex>
        <div className="mb-3" style={{ fontSize: 12, lineHeight: '18px', color: 'rgb(107, 114, 128)' }}>
          {t('availability.helper')}
        </div>
        <AvailabilityRow
          icon={visible ? Eye : EyeOff}
          iconBg="rgb(243, 244, 246)"
          iconColor="rgb(17, 24, 39)"
          title={t('form.is_visible.title')}
          titleWeight={600}
          subtitle={t(visible ? 'form.is_visible.subtitle_on' : 'form.is_visible.subtitle_off')}
          checked={visible}
          onChange={(checked) => update('is_visible', checked)}
        />
        <Divider style={{ marginLeft: 32, marginTop: 10, marginBottom: 0 }} />
        <AvailabilityRow
          icon={ShoppingBag}
          iconBg="rgb(249, 250, 251)"
          iconColor="rgb(107, 114, 128)"
          title={t('form.is_available_for_pickup.label')}
          titleWeight={500}
          subtitle={t(channelSubtitleKey('is_available_for_pickup', pickup))}
          checked={pickup}
          onChange={(checked) => update('is_available_for_pickup', checked)}
          disabled={!visible}
          indent
        />
        <AvailabilityRow
          icon={Bike}
          iconBg="rgb(249, 250, 251)"
          iconColor="rgb(107, 114, 128)"
          title={t('form.is_available_for_delivery.label')}
          titleWeight={500}
          subtitle={t(channelSubtitleKey('is_available_for_delivery', delivery))}
          checked={delivery}
          onChange={(checked) => update('is_available_for_delivery', checked)}
          disabled={!visible}
          indent
        />
        {showNoChannelsWarning && (
          <Flex
            align="flex-start"
            gap={8}
            className="mt-4"
            style={{
              padding: '10px 12px',
              borderRadius: 8,
              backgroundColor: 'rgb(254, 243, 199)',
              color: 'rgb(146, 64, 14)',
              fontSize: 12,
              lineHeight: '18px',
            }}
          >
            <AlertTriangle size={14} strokeWidth={2} style={{ marginTop: 2, flexShrink: 0 }} />
            <span>{t('availability.warning.no_channels')}</span>
          </Flex>
        )}
      </div>
    </Form.Item>
  );
}

function AvailabilityRow({
  icon: Icon,
  iconBg,
  iconColor,
  title,
  titleWeight,
  subtitle,
  checked,
  onChange,
  disabled = false,
  indent = false,
}) {
  return (
    <Flex
      align="center"
      style={{
        paddingTop: 10,
        paddingBottom: 6,
        paddingLeft: indent ? 32 : 0,
        opacity: disabled ? 0.45 : 1,
      }}
    >
      <Flex
        align="center"
        justify="center"
        style={{
          width: 32,
          height: 32,
          borderRadius: 6,
          backgroundColor: iconBg,
          color: iconColor,
          flexShrink: 0,
        }}
      >
        <Icon size={17} strokeWidth={1.7} />
      </Flex>
      <div className="flex-1" style={{ marginLeft: 14 }}>
        <div
          style={{
            fontSize: 13.5,
            fontWeight: titleWeight,
            lineHeight: '16px',
            color: 'rgb(17, 24, 39)',
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 11.5,
            lineHeight: '14px',
            color: 'rgb(156, 163, 175)',
            marginTop: 2,
          }}
        >
          {subtitle}
        </div>
      </div>
      <Switch checked={checked} onChange={onChange} disabled={disabled} />
    </Flex>
  );
}
